import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EVENT_COLORS } from "@/lib/eventColors";
import api from "@/lib/api";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Format a JS Date as YYYY-MM-DD in LOCAL time (avoids UTC drift)
const fmtLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// PhotoSnapshot — cycles through a date's photos every 3 seconds.
// Adds a subtle pulse of life to the calendar without being noisy.
function PhotoSnapshot({ photos }) {
  const [idx, setIdx] = useState(0);
  const API = process.env.REACT_APP_BACKEND_URL || "";
  useEffect(() => {
    if (photos.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % photos.length), 3000);
    return () => clearInterval(id);
  }, [photos.length]);
  if (!photos.length) return null;
  return (
    <div
      className="bd-cal-photostrip"
      style={{
        marginTop: "0.25rem",
        height: "32px",
        borderRadius: "6px",
        overflow: "hidden",
        position: "relative",
        boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
      }}
      data-testid="cal-photo-strip"
    >
      <img
        src={`${API}${photos[idx].url}`}
        alt=""
        className="w-full h-full object-cover"
        style={{ transition: "opacity 600ms ease" }}
        loading="lazy"
      />
      {photos.length > 1 && (
        <div
          style={{
            position: "absolute", bottom: 2, right: 4,
            fontSize: 9, color: "white",
            textShadow: "0 1px 2px rgba(0,0,0,0.7)",
            fontWeight: 700,
          }}
        >
          {idx + 1}/{photos.length}
        </div>
      )}
    </div>
  );
}

export default function CalendarMonth({ year, month, events, onPrev, onNext, onDayClick, onEventClick, schedulingMode = false }) {
  const today = new Date();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startWeekday = first.getDay();
  const daysInMonth = last.getDate();
  const monthName = first.toLocaleString("default", { month: "long" });

  const [photosByDate, setPhotosByDate] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/calendar/photo-strip");
        if (!cancelled) setPhotosByDate(data || {});
      } catch {
        if (!cancelled) setPhotosByDate({});
      }
    })();
    return () => { cancelled = true; };
  }, [year, month]);

  // build 6-week grid
  const grid = useMemo(() => {
    const cells = [];
    for (let i = 0; i < startWeekday; i++) {
      const d = new Date(year, month, 0 - (startWeekday - i - 1));
      cells.push({ date: d, dim: true });
    }
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), dim: false });
    while (cells.length % 7 !== 0) {
      const last2 = cells[cells.length - 1].date;
      const d = new Date(last2.getFullYear(), last2.getMonth(), last2.getDate() + 1);
      cells.push({ date: d, dim: true });
    }
    return cells;
  }, [year, month, startWeekday, daysInMonth]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.date) continue;
      const key = e.date.slice(0, 10);
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [events]);

  return (
    <div
      className={`bd-surface p-4 sm:p-6 ${schedulingMode ? "bd-scheduling" : ""}`}
      data-testid="calendar-month"
      data-scheduling={schedulingMode ? "1" : "0"}
    >
      <div className="flex items-center justify-between mb-4 bd-cal-header">
        <div className="flex items-center gap-3">
          {schedulingMode && (
            <span className="bd-scheduling-brand" data-testid="scheduling-brand">
              <span style={{ width: 6, height: 6, borderRadius: 9999, background: "#00E5FF", boxShadow: "0 0 8px #00E5FF" }} />
              Bornday
            </span>
          )}
          <h2
            className="text-2xl sm:text-3xl font-bold"
            style={{
              letterSpacing: schedulingMode ? "0.02em" : "-0.04em",
              fontFamily: schedulingMode ? "var(--font-mono, ui-monospace, SFMono-Regular, monospace)" : undefined,
            }}
            data-testid="calendar-title"
          >
            {monthName} <span style={{ color: "var(--text-dim)" }}>{year}</span>
          </h2>
        </div>
        <div className="flex gap-2">
          <button onClick={onPrev} className="bd-btn bd-btn-ghost" data-testid="calendar-prev" aria-label="Previous month">
            <ChevronLeft size={16} />
          </button>
          <button onClick={onNext} className="bd-btn bd-btn-ghost" data-testid="calendar-next" aria-label="Next month">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2 text-xs font-semibold bd-cal-dow" style={{ color: "var(--text-dim)" }}>
        {DOW.map((d) => (
          <div key={d} className="px-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {grid.map(({ date, dim }, i) => {
          const key = fmtLocal(date);
          const dayEvents = eventsByDay[key] || [];
          const isToday = fmtLocal(date) === fmtLocal(today);
          return (
            <div
              key={key}
              className={`bd-cal-day ${isToday ? "today" : ""} ${dim ? "dim" : ""}`}
              onClick={() => onDayClick && onDayClick(date, dayEvents)}
              data-testid={`calendar-day-${key}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{date.getDate()}</span>
                {dayEvents.length > 0 && (
                  <span className="bd-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
                    {dayEvents.length}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {dayEvents.slice(0, 5).map((e) => {
                  const color = EVENT_COLORS[e.event_type] || EVENT_COLORS.general;
                  return (
                    <button
                      key={`${e.event_id}-${e.date}`}
                      className="text-left truncate text-xs px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        background: color,
                        color: "#fff",
                        textShadow: "0 1px 1px rgba(0,0,0,0.25)",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                      }}
                      onClick={(ev) => {
                        // Forward to day-click so the popover lists every
                        // event on this day with edit/delete controls.
                        ev.stopPropagation();
                        onDayClick && onDayClick(date, dayEvents);
                      }}
                      data-testid={`calendar-event-${e.event_id}`}
                    >
                      {e.title}
                    </button>
                  );
                })}
                {dayEvents.length > 5 && (
                  <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>
                    +{dayEvents.length - 5} more
                  </span>
                )}
              </div>
              {(photosByDate[key] || []).length > 0 && (
                <PhotoSnapshot photos={photosByDate[key]} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { EVENT_COLORS };

